/**
 * The BIM section cut's own arithmetic (card t_94a47f54).
 *
 * The cut is a THREE clipping plane the RENDERER applies to fragments, and
 * THREE.Raycaster knows nothing about renderer clipping: geometry the user can
 * no longer see still produces hits. With the cut on, the removed roof and
 * upper walls kept "occluding" every widget inside a cut-open room, so the
 * dollhouse view showed none of them (measured: 3/69 visible).
 *
 * Both halves of the app's answer to "what does the renderer still show" — the
 * occlusion raycast's hits and the widget pins' own anchors — need the same
 * rule, so it lives here and is tested on its own (`bimClip.test.ts`) instead
 * of being reasoned about inside the raycast.
 */

/** The three.js `Plane` shape this needs — no import, so the rule stays pure. */
export interface ClipPlaneLike {
  /** Plane normal. Points at the side that is KEPT. */
  normal: { x: number; y: number; z: number };
  /** Plane offset along the normal (three's `Plane.constant`). */
  constant: number;
}

export interface ClipPointLike {
  x: number;
  y: number;
  z: number;
}

/**
 * True when the renderer removes this point from the picture.
 *
 * three discards every fragment whose SIGNED DISTANCE to a clipping plane is
 * negative — `clipping_planes_fragment`: `if ( dot( vClipPosition, plane.xyz )
 * > plane.w ) discard;` with `vClipPosition = -mvPosition.xyz` and the plane's
 * normal/constant as its view-space uniform — i.e. the same test as
 * `Plane.distanceToPoint(p) < 0` (`math/Plane.js`: `normal.dot(point) + constant`).
 * A point exactly ON the plane has distance 0 and is kept.
 *
 * A non-finite distance reads as KEPT: a broken coordinate must never hide a
 * widget, so a NaN falls on the visible side of the decision.
 */
export function isPointRemovedByClip(point: ClipPointLike, plane: ClipPlaneLike): boolean {
  const distance =
    plane.normal.x * point.x + plane.normal.y * point.y + plane.normal.z * point.z + plane.constant;
  return !Number.isNaN(distance) && distance < 0;
}

/**
 * The same rule over the section cut's WHOLE range: a cut is two planes now — one
 * above (everything over the storey's ceiling goes) and one below (everything
 * under its floor goes), so a storey can be isolated rather than only opened from
 * the top. A point is removed when ANY plane removes it; no planes (the cut is
 * off) removes nothing.
 */
export function isPointRemovedByAnyClip(
  point: ClipPointLike,
  planes: readonly ClipPlaneLike[] | null | undefined,
): boolean {
  if (!planes || planes.length === 0) return false;
  return planes.some((plane) => isPointRemovedByClip(point, plane));
}

/**
 * The same rule applied to a raycast's hit list (card t_247ec0f9): the hits the
 * renderer still shows, in the raycaster's own order.
 *
 * `THREE.Raycaster` knows nothing about the renderer's clipping planes, so with
 * the section cut on every pick (`raycastFromPointer`, the widget drag-drop's
 * cast) met the removed roof/facade first and acted on it: a click on a lamp
 * visible in a cut-open room selected the phantom slab in front of it. The
 * first hit the renderer still shows is the one a pick must use.
 *
 * A single plane is accepted as well as a list — the callers that predate the
 * bottom cut pass one — and no plane at all (the cut is off) is a pass-through,
 * and so is every input hit: no plane means no filtering, never an empty list.
 * Order is preserved so the caller's "first hit" stays the nearest one the user
 * can see.
 */
export function filterUnclippedHits<T extends { point: ClipPointLike }>(
  hits: T[],
  planes: readonly ClipPlaneLike[] | ClipPlaneLike | null,
): T[] {
  if (!planes) return hits;
  const list = Array.isArray(planes) ? planes : [planes as ClipPlaneLike];
  if (list.length === 0) return hits;
  return hits.filter((hit) => !isPointRemovedByAnyClip(hit.point, list));
}
